import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock, User, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const pinSchema = z.object({
  username: z.string().min(1, 'Name is required'),
  pin: z.string().length(4, 'PIN must be 4 digits').regex(/^\d{4}$/, 'PIN must be 4 digits'),
});

const emailSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(1, 'Password is required'),
});

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('pin'); // pin first

  // Show inactivity message if logged out due to timeout
  const [inactivityMsg] = useState(() => {
    const reason = localStorage.getItem('logout_reason');
    if (reason === 'inactivity') {
      localStorage.removeItem('logout_reason');
      return true;
    }
    return false;
  });

  const pinForm = useForm({ resolver: zodResolver(pinSchema), defaultValues: { username: '', pin: '' } });
  const emailForm = useForm({ resolver: zodResolver(emailSchema), defaultValues: { email: '', password: '' } });

  const handleLogin = async (data) => {
    setLoading(true);
    try {
      if (mode === 'pin') {
        await login({ username: data.username, pin: data.pin });
      } else {
        await login({ email: data.email, password: data.password });
      }
      toast.success('Welcome back!');
      // Honor ?next=... from the original deep link (e.g., CRM Bridge → /scheduling)
      const params = new URLSearchParams(window.location.search || '');
      const next = params.get('next');
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        // Strip ?next= from current search but preserve other params (like ?embedded=1)
        params.delete('next');
        const rest = params.toString();
        const sep = next.includes('?') ? '&' : '?';
        navigate(next + (rest ? sep + rest : ''));
      } else {
        navigate('/' + (window.location.search || ''));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <Card className="shadow-lg border-slate-200">
          <CardContent className="pt-8 pb-6 px-6">
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold text-slate-900">Sign In</h1>
            </div>

            {inactivityMsg && (
              <div className="mb-4 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-center" data-testid="inactivity-notice">
                <p className="text-xs font-medium text-amber-700">You were signed out due to inactivity</p>
              </div>
            )}

            {/* Mode Toggle */}
            <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5 mb-6">
              <button onClick={() => setMode('pin')} className={cn('flex-1 py-2 text-sm font-medium rounded-md transition-all', mode === 'pin' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')} data-testid="pin-tab">
                Quick PIN
              </button>
              <button onClick={() => setMode('email')} className={cn('flex-1 py-2 text-sm font-medium rounded-md transition-all', mode === 'email' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')} data-testid="email-tab">
                Email Login
              </button>
            </div>

            {mode === 'pin' ? (
              <form onSubmit={pinForm.handleSubmit(handleLogin)} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 text-sm">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input placeholder="Your name" className="pl-10 h-11" data-testid="username-input" {...pinForm.register('username')} />
                  </div>
                  {pinForm.formState.errors.username && <p className="text-sm text-destructive">{pinForm.formState.errors.username.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 text-sm">PIN</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="password" maxLength={4} placeholder="----" className="pl-10 h-11 tracking-[0.5em] text-center text-lg" data-testid="pin-input" {...pinForm.register('pin')} />
                  </div>
                  {pinForm.formState.errors.pin && <p className="text-sm text-destructive">{pinForm.formState.errors.pin.message}</p>}
                </div>
                <Button type="submit" className="w-full h-11" disabled={loading} data-testid="pin-login-btn">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Sign In'}
                </Button>
              </form>
            ) : (
              <form onSubmit={emailForm.handleSubmit(handleLogin)} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="email" placeholder="you@company.com" className="pl-10 h-11" data-testid="email-input" {...emailForm.register('email')} />
                  </div>
                  {emailForm.formState.errors.email && <p className="text-sm text-destructive">{emailForm.formState.errors.email.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 text-sm">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type={showPassword ? 'text' : 'password'} placeholder="Enter password" className="pl-10 pr-10 h-11" data-testid="password-input" {...emailForm.register('password')} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {emailForm.formState.errors.password && <p className="text-sm text-destructive">{emailForm.formState.errors.password.message}</p>}
                </div>
                <Button type="submit" className="w-full h-11" disabled={loading} data-testid="email-login-btn">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Sign In'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default LoginPage;
